/**
 * Client-side video validation, metadata probing, and poster frame extraction
 * for 360° video scenes in Web-Twin Pro.
 *
 * Rules:
 * - Strict 300,000,000 byte limit (matching backend TOUR_360_MAX_VIDEO_SIZE_BYTES).
 * - Strict 5-minute (300 seconds) duration limit (matching backend TOUR_360_MAX_VIDEO_DURATION_SECONDS).
 * - Supported format: MP4 container (H.264 video / AAC audio).
 * - Equirectangular 360° projection detection (typically 2:1 aspect ratio).
 * - Canvas-based poster frame extraction with memory safety and fallback.
 */

export const MAX_VIDEO_SIZE_BYTES = 300_000_000;
export const MAX_VIDEO_DURATION_SECONDS = 300;
export const ACCEPTED_VIDEO_TYPES = ["video/mp4"];
export const POSTER_SEEK_TIME_SECONDS = 0.5;
export const POSTER_MAX_WIDTH = 2048;
export const POSTER_MAX_HEIGHT = 1024;
export const POSTER_QUALITY = 0.85;

/**
 * Custom error class for video probe operations with typed error codes.
 */
export class VideoProbeError extends Error {
  constructor(message, code = "PROBE_ERROR", details = null) {
    super(message);
    this.name = "VideoProbeError";
    this.code = code;
    this.details = details;
  }
}

/**
 * Format bytes into human-readable string (e.g. "286.1 MB").
 */
export const formatBytes = (bytes, decimals = 1) => {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 Bytes";
  const k = 1024;
  const dm = Math.max(0, decimals);
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i] || "MB"}`;
};

/**
 * Format duration in seconds to "m:ss" format.
 */
export const formatDuration = totalSeconds => {
  if (!Number.isFinite(totalSeconds) || totalSeconds < 0) return "0:00";
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = Math.floor(totalSeconds % 60);
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
};

/**
 * Validate video file existence, format, and strict size limit.
 *
 * @param {File|Blob} file
 * @returns {{ valid: boolean, error: string | null }}
 */
export const validateVideoFile = file => {
  if (!file) {
    return {
      valid: false,
      error: "No video file provided.",
    };
  }

  const name = typeof file.name === "string" ? file.name.toLowerCase() : "";
  const type = typeof file.type === "string" ? file.type.toLowerCase() : "";
  const isMp4 = type === "video/mp4" || name.endsWith(".mp4");

  if (!isMp4) {
    return {
      valid: false,
      error: "Unsupported file type. Only MP4 (H.264 / AAC) 360° videos are supported.",
    };
  }

  if (typeof file.size === "number") {
    if (file.size <= 0) {
      return {
        valid: false,
        error: "Selected video file is empty (0 bytes).",
      };
    }
    if (file.size > MAX_VIDEO_SIZE_BYTES) {
      return {
        valid: false,
        error: `Video file exceeds the strict 300 MB limit (${formatBytes(file.size)}). Maximum allowed size is 300 MB (${MAX_VIDEO_SIZE_BYTES.toLocaleString()} bytes).`,
      };
    }
  }

  return { valid: true, error: null };
};

/**
 * Probe a video file using an offscreen HTMLVideoElement to inspect
 * duration, dimensions, equirectangular aspect ratio, and resolution.
 *
 * @param {File|Blob} file
 * @param {{ timeoutMs?: number, signal?: AbortSignal }} [options]
 * @returns {Promise<{
 *   duration: number,
 *   width: number,
 *   height: number,
 *   aspectRatio: number,
 *   isEquirectangular: boolean,
 *   isHighResolution: boolean,
 *   warnings: string[]
 * }>}
 */
export const probeVideoMetadata = (file, { timeoutMs = 15000, signal } = {}) => {
  const validation = validateVideoFile(file);
  if (!validation.valid) {
    return Promise.reject(
      new VideoProbeError(
        validation.error,
        file?.size > MAX_VIDEO_SIZE_BYTES ? "SIZE_EXCEEDED" : "FILE_INVALID"
      )
    );
  }

  if (typeof document === "undefined") {
    return Promise.reject(
      new VideoProbeError("Video probing is only supported in browser environments.", "ENV_ERROR")
    );
  }

  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      return reject(new VideoProbeError("Probe aborted.", "ABORTED"));
    }

    const video = document.createElement("video");
    video.preload = "metadata";
    video.muted = true;
    video.playsInline = true;

    let objectUrl = null;
    let timer = null;

    const cleanup = () => {
      if (timer) clearTimeout(timer);
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
        objectUrl = null;
      }
      video.removeAttribute("src");
      video.load();
      video.removeEventListener("loadedmetadata", onLoadedMetadata);
      video.removeEventListener("error", onError);
      if (signal) signal.removeEventListener("abort", onAbort);
    };

    const onAbort = () => {
      cleanup();
      reject(new VideoProbeError("Video probe aborted by user.", "ABORTED"));
    };

    const onError = () => {
      const mediaError = video.error;
      let msg = "Could not decode video metadata. Ensure the file is an H.264 MP4 video.";
      if (mediaError?.code === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED) {
        msg = "The video format or codec is not supported by your browser. Use H.264 MP4.";
      }
      cleanup();
      reject(new VideoProbeError(msg, "DECODE_ERROR", mediaError));
    };

    const onLoadedMetadata = () => {
      const duration = video.duration;
      const width = video.videoWidth;
      const height = video.videoHeight;

      if (!Number.isFinite(duration) || duration <= 0) {
        cleanup();
        return reject(
          new VideoProbeError("Could not determine video duration.", "INVALID_METADATA")
        );
      }

      if (duration > MAX_VIDEO_DURATION_SECONDS) {
        cleanup();
        return reject(
          new VideoProbeError(
            `Video duration (${formatDuration(duration)}) exceeds the maximum allowed 5 minutes (5:00).`,
            "DURATION_EXCEEDED",
            { duration, maxDuration: MAX_VIDEO_DURATION_SECONDS }
          )
        );
      }

      if (!width || !height) {
        cleanup();
        return reject(
          new VideoProbeError("Could not determine video dimensions.", "INVALID_METADATA")
        );
      }

      const aspectRatio = width / height;
      // Standard equirectangular 360 projection has 2:1 aspect ratio
      const isEquirectangular = aspectRatio >= 1.85 && aspectRatio <= 2.15;
      const isHighResolution = width >= 3840 || height >= 1920;

      const warnings = [];
      if (!isEquirectangular) {
        warnings.push(
          `Video aspect ratio is ${aspectRatio.toFixed(2)}:1. Recommended 360° equirectangular video ratio is 2:1 (e.g. 3840×1920).`
        );
      }
      if (width < 1920 || height < 960) {
        warnings.push(
          `Video resolution (${width}×${height}) is low for 360° viewing. At least 1920×960 is recommended.`
        );
      }

      cleanup();
      resolve({
        duration,
        width,
        height,
        aspectRatio,
        isEquirectangular,
        isHighResolution,
        warnings,
      });
    };

    if (signal) signal.addEventListener("abort", onAbort, { once: true });
    video.addEventListener("loadedmetadata", onLoadedMetadata, { once: true });
    video.addEventListener("error", onError, { once: true });

    timer = setTimeout(() => {
      cleanup();
      reject(
        new VideoProbeError(
          "Timeout probing video metadata. The file may be corrupt or unsupported.",
          "TIMEOUT"
        )
      );
    }, timeoutMs);

    try {
      objectUrl = URL.createObjectURL(file);
      video.src = objectUrl;
      if (video.readyState >= 1) {
        onLoadedMetadata();
      }
    } catch (err) {
      cleanup();
      reject(new VideoProbeError(`Failed to load video file: ${err.message}`, "LOAD_ERROR", err));
    }
  });
};

/**
 * Generate a guaranteed valid 1x1 base64 JPEG blob if canvas encoding is unavailable.
 */
const createMinimal1x1JpegBlob = () => {
  try {
    const b64 =
      "/9j/4AAQSkZJRgABAQEASABIAAD/2wBDAP//////////////////////////////////////////////////////////////////////////////////////wgALCAABAAEBAREA/8QAFBABAAAAAAAAAAAAAAAAAAAAAP/aAAgBAQABPxA=";
    const binary = atob(b64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return new Blob([bytes], { type: "image/jpeg" });
  } catch {
    return new Blob([new Uint8Array([0xff, 0xd8, 0xff, 0xd9])], { type: "image/jpeg" });
  }
};

/**
 * Generate a fallback poster frame using an equirectangular-styled canvas gradient
 * if hardware or codec decode fails during poster frame extraction.
 *
 * @param {number} width
 * @param {number} height
 * @returns {Promise<Blob>}
 */
const generateFallbackPosterBlob = (width = 2048, height = 1024) => {
  return new Promise(resolve => {
    try {
      const canvas = document.createElement("canvas");
      canvas.width = Math.min(width || 2048, POSTER_MAX_WIDTH);
      canvas.height = Math.min(height || 1024, POSTER_MAX_HEIGHT);
      const ctx = canvas.getContext("2d");

      if (ctx) {
        // Equirectangular gradient representing sky to horizon to ground
        const grad = ctx.createLinearGradient(0, 0, 0, canvas.height);
        grad.addColorStop(0, "#1a2234");
        grad.addColorStop(0.5, "#2d3748");
        grad.addColorStop(1, "#111827");
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Subtle center guideline
        ctx.strokeStyle = "rgba(255, 255, 255, 0.15)";
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(0, canvas.height / 2);
        ctx.lineTo(canvas.width, canvas.height / 2);
        ctx.stroke();

        // 360 Video indicator badge in center
        ctx.fillStyle = "rgba(0, 0, 0, 0.5)";
        const badgeW = 240;
        const badgeH = 64;
        const badgeX = (canvas.width - badgeW) / 2;
        const badgeY = (canvas.height - badgeH) / 2;
        ctx.beginPath();
        ctx.roundRect?.(badgeX, badgeY, badgeW, badgeH, 12);
        ctx.fill();

        ctx.fillStyle = "#ffffff";
        ctx.font = "bold 24px sans-serif";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText("360° Video", canvas.width / 2, canvas.height / 2);
      }

      canvas.toBlob(
        blob => {
          canvas.width = 0;
          canvas.height = 0;
          if (blob && blob.size > 0) {
            resolve(blob);
          } else {
            resolve(createMinimal1x1JpegBlob());
          }
        },
        "image/jpeg",
        POSTER_QUALITY
      );
    } catch {
      resolve(createMinimal1x1JpegBlob());
    }
  });
};

/**
 * Compute the optimal poster seek timestamp given video duration and preferred seek time.
 * - For videos longer than requestedSeek, returns requestedSeek (default 0.5s).
 * - For shorter videos (e.g. <= 0.5s), returns a safe fractional timestamp (e.g. 25% of duration).
 * - For zero or non-finite duration, returns 0.
 *
 * @param {number} duration
 * @param {number} [requestedSeek]
 * @returns {number}
 */
export const computePosterTimestamp = (duration, requestedSeek = POSTER_SEEK_TIME_SECONDS) => {
  if (!Number.isFinite(duration) || duration <= 0) return 0;
  if (duration <= requestedSeek) {
    return Math.max(0, Number((duration * 0.25).toFixed(3)));
  }
  return Math.min(requestedSeek, Math.max(0, Number((duration - 0.05).toFixed(3))));
};

/**
 * Extract a high-quality poster frame from a 360° video file at a specified seek time.
 * Scales down dimensions if they exceed maxWidth / maxHeight while preserving aspect ratio.
 *
 * Uses direct HTMLVideoElement seek and loadeddata events rather than requestVideoFrameCallback,
 * preventing compositor stalls on offscreen/detached video elements in Chrome.
 *
 * @param {File|Blob} file
 * @param {{
 *   seekTime?: number,
 *   maxWidth?: number,
 *   maxHeight?: number,
 *   quality?: number,
 *   timeoutMs?: number,
 *   signal?: AbortSignal,
 *   knownDuration?: number | null
 * }} [options]
 * @returns {Promise<{
 *   posterBlob: Blob,
 *   posterUrl: string,
 *   width: number,
 *   height: number
 * }>}
 */
export const extractPosterFrame = (
  file,
  {
    seekTime = POSTER_SEEK_TIME_SECONDS,
    maxWidth = POSTER_MAX_WIDTH,
    maxHeight = POSTER_MAX_HEIGHT,
    quality = POSTER_QUALITY,
    timeoutMs = 15000,
    signal,
    knownDuration = null,
  } = {}
) => {
  const validation = validateVideoFile(file);
  if (!validation.valid) {
    return Promise.reject(new VideoProbeError(validation.error, "FILE_INVALID"));
  }

  if (typeof document === "undefined") {
    return Promise.reject(
      new VideoProbeError("Poster extraction is only supported in browser environments.", "ENV_ERROR")
    );
  }

  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      return reject(new VideoProbeError("Poster extraction aborted.", "ABORTED"));
    }

    const video = document.createElement("video");
    video.muted = true;
    video.playsInline = true;
    video.preload = "auto";

    let objectUrl = null;
    let overallTimeoutTimer = null;
    let seekFallbackTimer = null;
    let drawTimer = null;
    let isCleanedUp = false;
    let currentStage = "INITIALIZING";
    let targetSeekTime = 0;

    const cleanup = () => {
      isCleanedUp = true;
      if (overallTimeoutTimer) {
        clearTimeout(overallTimeoutTimer);
        overallTimeoutTimer = null;
      }
      if (seekFallbackTimer) {
        clearTimeout(seekFallbackTimer);
        seekFallbackTimer = null;
      }
      if (drawTimer) {
        clearTimeout(drawTimer);
        drawTimer = null;
      }
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
        objectUrl = null;
      }
      video.removeEventListener("loadedmetadata", onMetadata);
      video.removeEventListener("loadeddata", onDataReady);
      video.removeEventListener("canplay", onDataReady);
      video.removeEventListener("seeked", onSeekComplete);
      video.removeEventListener("error", onError);
      if (signal) signal.removeEventListener("abort", onAbort);
      try {
        video.removeAttribute("src");
        video.load();
      } catch {
        /* ignore */
      }
    };

    const onAbort = () => {
      cleanup();
      reject(new VideoProbeError("Poster extraction aborted by user.", "ABORTED"));
    };

    const onError = async () => {
      if (isCleanedUp || signal?.aborted) return;
      const mediaError = video.error;
      let errorMsg = "Could not decode video for poster frame.";
      if (mediaError?.code === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED) {
        errorMsg = "The video format or codec is not supported by your browser. Use H.264 MP4.";
      } else if (mediaError?.code === MediaError.MEDIA_ERR_DECODE) {
        errorMsg = "Video decoding error. The file may be corrupt or encoded with an unsupported profile.";
      }

      try {
        const fallbackBlob = await generateFallbackPosterBlob(maxWidth, maxHeight);
        if (isCleanedUp || signal?.aborted) return;
        const fallbackUrl = URL.createObjectURL(fallbackBlob);
        cleanup();
        resolve({
          posterBlob: fallbackBlob,
          posterUrl: fallbackUrl,
          width: maxWidth,
          height: maxHeight,
        });
      } catch (err) {
        cleanup();
        reject(new VideoProbeError(errorMsg, "DECODE_ERROR", mediaError));
      }
    };

    const captureCanvasFrame = () => {
      if (isCleanedUp || signal?.aborted) return;
      currentStage = "CAPTURING";

      try {
        let targetWidth = video.videoWidth || maxWidth;
        let targetHeight = video.videoHeight || maxHeight;

        if (targetWidth > maxWidth || targetHeight > maxHeight) {
          const scale = Math.min(maxWidth / targetWidth, maxHeight / targetHeight);
          targetWidth = Math.max(1, Math.round(targetWidth * scale));
          targetHeight = Math.max(1, Math.round(targetHeight * scale));
        }

        const canvas = document.createElement("canvas");
        canvas.width = targetWidth;
        canvas.height = targetHeight;
        const ctx = canvas.getContext("2d");

        if (!ctx) {
          throw new Error("Unable to obtain 2D canvas rendering context.");
        }

        ctx.drawImage(video, 0, 0, targetWidth, targetHeight);

        canvas.toBlob(
          async blob => {
            if (isCleanedUp || signal?.aborted) {
              canvas.width = 0;
              canvas.height = 0;
              return;
            }

            if (blob && blob.size > 0) {
              const posterUrl = URL.createObjectURL(blob);
              canvas.width = 0;
              canvas.height = 0;
              cleanup();
              resolve({
                posterBlob: blob,
                posterUrl,
                width: targetWidth,
                height: targetHeight,
              });
            } else {
              const fallbackBlob = await generateFallbackPosterBlob(targetWidth, targetHeight);
              if (isCleanedUp || signal?.aborted) {
                canvas.width = 0;
                canvas.height = 0;
                return;
              }
              const fallbackUrl = URL.createObjectURL(fallbackBlob);
              canvas.width = 0;
              canvas.height = 0;
              cleanup();
              resolve({
                posterBlob: fallbackBlob,
                posterUrl: fallbackUrl,
                width: targetWidth,
                height: targetHeight,
              });
            }
          },
          "image/jpeg",
          quality
        );
      } catch (drawErr) {
        generateFallbackPosterBlob(video.videoWidth || maxWidth, video.videoHeight || maxHeight)
          .then(fallbackBlob => {
            if (isCleanedUp || signal?.aborted) return;
            const fallbackUrl = URL.createObjectURL(fallbackBlob);
            cleanup();
            resolve({
              posterBlob: fallbackBlob,
              posterUrl: fallbackUrl,
              width: video.videoWidth || maxWidth,
              height: video.videoHeight || maxHeight,
            });
          })
          .catch(fallbackErr => {
            cleanup();
            reject(
              new VideoProbeError(
                `Failed to capture poster frame: ${drawErr.message}`,
                "POSTER_CAPTURE_FAILED",
                fallbackErr
              )
            );
          });
      }
    };

    const triggerCapture = () => {
      if (isCleanedUp || signal?.aborted) return;
      if (seekFallbackTimer) {
        clearTimeout(seekFallbackTimer);
        seekFallbackTimer = null;
      }
      // Small 20ms pause ensures decoded frame buffer availability across all browser engines
      drawTimer = setTimeout(() => {
        drawTimer = null;
        captureCanvasFrame();
      }, 20);
    };

    const onSeekComplete = () => {
      video.removeEventListener("seeked", onSeekComplete);
      if (isCleanedUp || signal?.aborted) return;
      triggerCapture();
    };

    const onDataReady = () => {
      video.removeEventListener("loadeddata", onDataReady);
      video.removeEventListener("canplay", onDataReady);
      if (isCleanedUp || signal?.aborted) return;
      triggerCapture();
    };

    const startSeeking = () => {
      if (isCleanedUp || signal?.aborted) return;
      const duration = knownDuration ?? video.duration ?? 0;
      targetSeekTime = computePosterTimestamp(duration, seekTime);

      // If target time is effectively 0, or video is already at the target
      if (targetSeekTime <= 0.01 || Math.abs(video.currentTime - targetSeekTime) <= 0.01) {
        currentStage = "WAITING_DATA";
        if (video.readyState >= 2) {
          triggerCapture();
        } else {
          video.addEventListener("loadeddata", onDataReady, { once: true });
          video.addEventListener("canplay", onDataReady, { once: true });
        }
        return;
      }

      currentStage = "SEEKING";
      video.addEventListener("seeked", onSeekComplete, { once: true });

      // Seek fallback timer: if seeking doesn't fire within 3.5s, capture whatever frame is available
      seekFallbackTimer = setTimeout(() => {
        seekFallbackTimer = null;
        if (isCleanedUp || signal?.aborted) return;
        video.removeEventListener("seeked", onSeekComplete);
        console.warn("[VideoProbe] Seeked event timed out; capturing available frame at readyState:", video.readyState);
        triggerCapture();
      }, 3500);

      try {
        video.currentTime = targetSeekTime;
      } catch (err) {
        if (seekFallbackTimer) {
          clearTimeout(seekFallbackTimer);
          seekFallbackTimer = null;
        }
        video.removeEventListener("seeked", onSeekComplete);
        triggerCapture();
      }
    };

    const onMetadata = () => {
      video.removeEventListener("loadedmetadata", onMetadata);
      if (isCleanedUp || signal?.aborted) return;
      startSeeking();
    };

    overallTimeoutTimer = setTimeout(() => {
      cleanup();
      let stageMsg = "Timeout extracting video poster frame.";
      let stageCode = "TIMEOUT";
      if (currentStage === "INITIALIZING" || currentStage === "LOADING_METADATA") {
        stageMsg = "Timeout reading video metadata. Ensure the file is a valid MP4 video.";
        stageCode = "METADATA_TIMEOUT";
      } else if (currentStage === "SEEKING") {
        stageMsg = `Timeout seeking video frame at ${targetSeekTime}s for poster extraction.`;
        stageCode = "SEEK_TIMEOUT";
      } else if (currentStage === "WAITING_DATA") {
        stageMsg = "Timeout waiting for initial video frame data.";
        stageCode = "FRAME_TIMEOUT";
      } else if (currentStage === "CAPTURING") {
        stageMsg = "Timeout capturing poster image from canvas.";
        stageCode = "CANVAS_TIMEOUT";
      }
      reject(new VideoProbeError(stageMsg, stageCode, { stage: currentStage }));
    }, timeoutMs);

    if (signal) {
      signal.addEventListener("abort", onAbort, { once: true });
    }

    currentStage = "LOADING_METADATA";
    video.addEventListener("loadedmetadata", onMetadata, { once: true });
    video.addEventListener("error", onError, { once: true });

    try {
      objectUrl = URL.createObjectURL(file);
      video.src = objectUrl;
      if (video.readyState >= 1) {
        onMetadata();
      }
    } catch (err) {
      cleanup();
      reject(new VideoProbeError(`Failed to load video for poster: ${err.message}`, "LOAD_ERROR", err));
    }
  });
};

/**
 * High-level helper: validates, probes metadata, checks duration,
 * and extracts an equirectangular poster frame in one call.
 *
 * @param {File} file
 * @param {Object} [options]
 * @returns {Promise<{
 *   file: File,
 *   metadata: {
 *     duration: number,
 *     width: number,
 *     height: number,
 *     aspectRatio: number,
 *     isEquirectangular: boolean,
 *     isHighResolution: boolean,
 *     warnings: string[]
 *   },
 *   posterBlob: Blob,
 *   posterUrl: string,
 *   posterWidth: number,
 *   posterHeight: number,
 *   warnings: string[]
 * }>}
 */
export const probeAndPrepareVideo = async (file, options = {}) => {
  const validation = validateVideoFile(file);
  if (!validation.valid) {
    throw new VideoProbeError(
      validation.error,
      file?.size > MAX_VIDEO_SIZE_BYTES ? "SIZE_EXCEEDED" : "FILE_INVALID"
    );
  }

  const metadata = await probeVideoMetadata(file, options);
  const poster = await extractPosterFrame(file, {
    seekTime: options.seekTime ?? POSTER_SEEK_TIME_SECONDS,
    maxWidth: options.maxWidth ?? POSTER_MAX_WIDTH,
    maxHeight: options.maxHeight ?? POSTER_MAX_HEIGHT,
    quality: options.quality ?? POSTER_QUALITY,
    timeoutMs: options.timeoutMs ?? 15000,
    signal: options.signal,
    knownDuration: metadata.duration,
  });

  const warnings = [...(metadata.warnings || [])];

  return {
    file,
    metadata,
    posterBlob: poster.posterBlob,
    posterUrl: poster.posterUrl,
    posterWidth: poster.width,
    posterHeight: poster.height,
    warnings,
  };
};

/**
 * Revoke any temporary object URLs created during video probing or poster extraction.
 *
 * @param {{ posterUrl?: string } | null} result
 */
export const revokeVideoProbeUrls = result => {
  if (
    result?.posterUrl &&
    typeof result.posterUrl === "string" &&
    result.posterUrl.startsWith("blob:")
  ) {
    URL.revokeObjectURL(result.posterUrl);
  }
};

